<script setup lang="ts">
import authContent from "~/content/auth.json";

interface Props {
  width?: string;
  redirect?: boolean | string;
  buttonText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  width: "100%",
  redirect: true,
  buttonText: "Login",
});

const authStore = useAuthStore();
const route = useRoute();

const email = ref("");
const password = ref("");

const emailInput = useTemplateRef<{ flush: () => void }>("emailInput");
const passwordInput = useTemplateRef<{ flush: () => void }>("passwordInput");

const submitting = ref(false);
const ssoEnabled = computed(() => route.query.sso === "true");

function handleEmailChange(field: { value: string }) {
  email.value = field.value;
}

function handlePasswordChange(field: { value: string }) {
  password.value = field.value;
}

async function handleLogin() {
  // Enter reaches here once, via the form's implicit submission. The TextInputs
  // deliberately don't also bind @enter-pressed — that was the second of the two
  // POSTs seen on production. Guard kept as a backstop for any other caller.
  if (submitting.value) return;

  // TextInput only hands its value up 500 ms after the last keystroke, so read
  // the refs only after flushing, or a fast Enter submits the previous value.
  emailInput.value?.flush();
  passwordInput.value?.flush();

  if (!email.value || !password.value) return;

  const redirect = typeof props.redirect === "string" ? props.redirect : undefined;

  submitting.value = true;
  try {
    await authStore.login({ email: email.value, password: password.value }, redirect);
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div
    class="login-form"
    :style="{ width }"
  >
    <form @submit.prevent="handleLogin">
      <TextInput
        ref="emailInput"
        :value="email"
        title="Email"
        type="email"
        :is-required="true"
        :place-holder="authContent.login.placeholders.email"
        autocomplete="email"
        :login="true"
        :index="0"
        @return-value="handleEmailChange"
      />
      <TextInput
        ref="passwordInput"
        :value="password"
        title="Password"
        type="password"
        :is-required="true"
        :place-holder="authContent.login.placeholders.password"
        autocomplete="current-password"
        :login="true"
        :index="1"
        @return-value="handlePasswordChange"
      />

      <BannerError :message="authStore.loginError || ''" />

      <a
        class="login-form__forgot"
        @click.prevent="navigateTo('/forgot')"
      >
        {{ authContent.login.forgotPassword }}
      </a>

      <div class="login-form__actions">
        <DynamicButton
          :submit="true"
          :text="buttonText"
          color="pink"
          width="100%"
          :spinning="authStore.authLoading"
          :disabled="!email || !password"
          :icon="authStore.authLoading ? 'spinner' : ''"
        />
      </div>

      <DynamicButton
        v-if="authStore.loginNeedsVerification && email"
        class="login-form__button"
        color="pink"
        width="100%"
        text="Request new verification code"
        @click-event="
          navigateTo(`/verify/new?email=${encodeURIComponent(email)}&type=EMAIL_VERIFICATION`)
        "
      />

      <DynamicButton
        class="login-form__button"
        color="white"
        width="100%"
        text="Create account"
        @click-event="navigateTo(`/sign-up/?redirect=${redirect}`)"
      />
    </form>

    <!--
      BOTH buttons are deliberately OUTSIDE the <form>, for two DIFFERENT reasons.
      Neither may be moved back in.

      SsoButton: it contains its own email input, and DynamicButton never sets the
      real DOM `disabled` attribute (only a CSS class), so implicit form submission
      is never suppressed — an Enter keypress in the SSO field fired handleLogin()
      as well as continueSso(), starting a password login and an SSO redirect at once.

      GoogleButton: WebKit blocks touch events into Google's cross-origin GSI iframe
      when it is nested inside a <form>, so the button is unusable on iOS; Blink
      (Android) tolerates it, which is why it only showed up on one platform (#203).

      Styling is unaffected — `.login-form__button` is a flat, non-scoped global
      rule, not a descendant selector.
    -->
    <SsoButton
      v-if="ssoEnabled"
      :redirect="redirect"
    />
    <GoogleButton :redirect="redirect" />
  </div>
</template>

<style lang="scss">
.login-form {
  &__forgot {
    float: right;
    margin: 1rem 0;
    color: var(--color-grey-1);
    text-decoration: none;
    cursor: pointer;
    font-size: 0.9rem;
    transition: color var(--dur-fast) var(--ease-out);
    &:hover {
      color: var(--color-pink-1);
      text-decoration: underline;
    }
  }

  &__button {
    margin-bottom: 1rem;
  }

  &__actions {
    margin: 1rem 0;
  }
}
</style>
